import {
    ArrowDownUp,
    BriefcaseBusiness,
    Building2,
    DollarSign,
    FileText,
    Search,
    Users,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { AddPricingConfigDialog, EditPricingConfigDialog } from './credit/pricingConfigDialogs';
import { AdminCreateCompanyModal } from './AdminCreateCompanyModal';
import { AdminCreateUserModal } from './AdminCreateUserModal';
import {
    AdminDashboardPaginatedSection,
    AdminDashboardTabButton,
} from './AdminDashboardPrimitives';
import {
    AdminCompaniesTable,
    AdminSowsTable,
    AdminUsersTable,
} from './AdminDashboardTableComponents';
import { AdminToast } from './AdminToast';
import {
    getAdminEmptyBrowseCopy,
    getAdminSearchPlaceholder,
    isImmediateSearchKey,
} from './adminDashboardHelpers';
import {
    PricingConfigActiveUsageDialog,
    PricingConfigArchiveConfirmDialog,
    AdminPricingTable,
} from './pricing/AdminPricingTable';
import { ProfessionalServicesTableSection } from './credit/ProfessionalServicesTableSection';
import {
    ProfessionalServiceDeleteConfirmDialog,
    ProfessionalServiceDialog,
} from './credit/professionalServicesDialogs';
import { AdminEditUserModal } from './AdminEditUserModal';
import { useAdminDashboardState, PAGE_SIZE } from './useAdminDashboardState';

export function AdminDashboard() {
    const dashboard = useAdminDashboardState();
    const pricingViewButtonClassName = (active: boolean) =>
        `inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-semibold text-neutral-950 transition-colors ${
            active ? 'border border-neutral-400 bg-white shadow-sm' : 'hover:bg-neutral-300/45'
        }`;

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="admin-tab-switcher-track">
                    <AdminDashboardTabButton
                        active={dashboard.activeTab === 'companies'}
                        onClick={() => dashboard.resetActiveTabPage('companies')}
                        icon={Building2}
                        label="Companies"
                    />
                    <AdminDashboardTabButton
                        active={dashboard.activeTab === 'users'}
                        onClick={() => dashboard.resetActiveTabPage('users')}
                        icon={Users}
                        label="Users"
                    />
                    <AdminDashboardTabButton
                        active={dashboard.activeTab === 'sows'}
                        onClick={() => dashboard.resetActiveTabPage('sows')}
                        icon={FileText}
                        label="SOWs"
                    />
                    <AdminDashboardTabButton
                        active={dashboard.activeTab === 'pricing'}
                        onClick={() => dashboard.resetActiveTabPage('pricing')}
                        icon={DollarSign}
                        label="Pricing Configs"
                    />
                    <AdminDashboardTabButton
                        active={dashboard.activeTab === 'professionalServices'}
                        onClick={() => dashboard.resetActiveTabPage('professionalServices')}
                        icon={BriefcaseBusiness}
                        label="Professional Services"
                    />
                </div>

                {dashboard.newActionLabel && (
                    <div className="flex shrink-0 gap-2">
                        <Button onClick={dashboard.openNewDialog}>
                            {dashboard.newActionLabel}
                        </Button>
                    </div>
                )}
            </div>

            <div className="rounded-xl border bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
                        <Input
                            value={dashboard.searchDraft}
                            onChange={(event) => dashboard.handleSearchChange(event.target.value)}
                            onKeyDown={(event) => {
                                if (isImmediateSearchKey(event.key)) {
                                    dashboard.commitSearch(event.currentTarget.value);
                                }
                            }}
                            placeholder={getAdminSearchPlaceholder(dashboard.activeTab)}
                            className="border-0 admin-field-fill pl-10 shadow-none focus-visible:ring-0"
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                        {dashboard.activeTab === 'companies' && (
                            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-neutral-700">
                                <input
                                    type="checkbox"
                                    checked={dashboard.activeCompaniesOnlyFilter}
                                    onChange={(event) =>
                                        dashboard.handleActiveCompaniesOnlyChange(
                                            event.target.checked,
                                        )
                                    }
                                    className="size-4 rounded border-neutral-300 text-[#276db0] focus:ring-[#276db0]"
                                />
                                Active companies only
                            </label>
                        )}
                        {dashboard.activeTab === 'users' && (
                            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-neutral-700">
                                <input
                                    type="checkbox"
                                    checked={dashboard.activeUsersOnlyFilter}
                                    onChange={(event) =>
                                        dashboard.handleActiveUsersOnlyChange(event.target.checked)
                                    }
                                    className="size-4 rounded border-neutral-300 text-[#276db0] focus:ring-[#276db0]"
                                />
                                Active users only
                            </label>
                        )}
                        {dashboard.activeTab === 'sows' && (
                            <>
                                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-neutral-700">
                                    <input
                                        type="checkbox"
                                        checked={dashboard.activeSowsOnlyFilter}
                                        onChange={(event) =>
                                            dashboard.handleActiveSowsOnlyChange(
                                                event.target.checked,
                                            )
                                        }
                                        className="size-4 rounded border-neutral-300 text-[#276db0] focus:ring-[#276db0]"
                                    />
                                    Hide expired and archived SOWs
                                </label>
                                <label className="flex flex-wrap items-center gap-2 text-sm text-neutral-700">
                                    <span
                                        className="inline-flex shrink-0 items-center justify-center text-neutral-700"
                                        title="Sort by"
                                    >
                                        <ArrowDownUp className="size-4" aria-hidden />
                                    </span>
                                    <Select
                                        ariaLabel="Sort SOWs list"
                                        className="min-w-[220px]"
                                        value={dashboard.sowSortBy}
                                        options={[
                                            {
                                                value: 'end_date',
                                                label: 'End date (earliest first)',
                                            },
                                            {
                                                value: 'pro_usage_percent',
                                                label: 'Pro usage % (highest first)',
                                            },
                                            {
                                                value: 'pulse_usage_percent',
                                                label: 'Pulse usage % (highest first)',
                                            },
                                        ]}
                                        onValueChange={dashboard.handleSowSortByChange}
                                    />
                                </label>
                            </>
                        )}
                        {dashboard.activeTab === 'pricing' && (
                            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-neutral-700">
                                <input
                                    type="checkbox"
                                    checked={dashboard.hideArchivedPricingFilter}
                                    onChange={(event) =>
                                        dashboard.handleHideArchivedPricingChange(
                                            event.target.checked,
                                        )
                                    }
                                    className="size-4 rounded border-neutral-300 text-[#276db0] focus:ring-[#276db0]"
                                />
                                Hide archived pricing versions
                            </label>
                        )}
                    </div>
                </div>
            </div>

            {!dashboard.shouldFetchActiveTab && (
                <Card>
                    <CardContent className="space-y-3 p-8 text-center">
                        <p className="text-sm text-neutral-600">
                            {getAdminEmptyBrowseCopy(dashboard.activeTab)}
                        </p>
                        <Button variant="outline" onClick={dashboard.handleBrowseAll}>
                            Browse All
                        </Button>
                    </CardContent>
                </Card>
            )}

            {dashboard.isLoading && (
                <Card>
                    <CardContent className="p-8 text-center text-sm text-neutral-500">
                        Loading admin data...
                    </CardContent>
                </Card>
            )}

            {dashboard.error && (
                <Card className="border-red-200 bg-red-50">
                    <CardContent className="p-4 text-sm text-red-800">
                        Failed to load admin dashboard data.
                    </CardContent>
                </Card>
            )}

            {dashboard.shouldFetchActiveTab &&
                !dashboard.isLoading &&
                !dashboard.error &&
                dashboard.activeTab === 'companies' && (
                    <AdminDashboardPaginatedSection
                        rows={dashboard.companyRows}
                        page={dashboard.activePage}
                        pageSize={PAGE_SIZE}
                        total={dashboard.activeData?.total ?? 0}
                        totalPages={dashboard.activeData?.totalPages ?? 1}
                        onPageChange={dashboard.setActivePage}
                        render={(rows) => (
                            <AdminCompaniesTable
                                rows={rows}
                                onRowClick={dashboard.handleCompanyRowClick}
                            />
                        )}
                    />
                )}
            {dashboard.shouldFetchActiveTab &&
                !dashboard.isLoading &&
                !dashboard.error &&
                dashboard.activeTab === 'sows' && (
                    <AdminDashboardPaginatedSection
                        rows={dashboard.sowRows}
                        page={dashboard.activePage}
                        pageSize={PAGE_SIZE}
                        total={dashboard.activeData?.total ?? 0}
                        totalPages={dashboard.activeData?.totalPages ?? 1}
                        onPageChange={dashboard.setActivePage}
                        render={(rows) => (
                            <AdminSowsTable rows={rows} onRowClick={dashboard.handleSowRowClick} />
                        )}
                    />
                )}
            {dashboard.shouldFetchActiveTab &&
                !dashboard.isLoading &&
                !dashboard.error &&
                dashboard.activeTab === 'users' && (
                    <AdminDashboardPaginatedSection
                        rows={dashboard.users}
                        page={dashboard.activePage}
                        pageSize={PAGE_SIZE}
                        total={dashboard.activeData?.total ?? 0}
                        totalPages={dashboard.activeData?.totalPages ?? 1}
                        onPageChange={dashboard.setActivePage}
                        render={(rows) => (
                            <AdminUsersTable
                                rows={rows}
                                syncingUserId={dashboard.syncingUserId}
                                onRowClick={dashboard.setSelectedUser}
                                onSync={dashboard.handleSyncUser}
                            />
                        )}
                    />
                )}
            {dashboard.shouldFetchActiveTab &&
                !dashboard.isLoading &&
                !dashboard.error &&
                dashboard.activeTab === 'pricing' && (
                    <div className="space-y-4">
                        <div className="admin-tab-switcher-track w-fit">
                            <button
                                type="button"
                                className={pricingViewButtonClassName(
                                    dashboard.pricingConfigsView === 'all',
                                )}
                                onClick={() => dashboard.setPricingConfigsView('all')}
                            >
                                All versions
                            </button>
                            <button
                                type="button"
                                className={pricingViewButtonClassName(
                                    dashboard.pricingConfigsView === 'grouped',
                                )}
                                onClick={() => dashboard.setPricingConfigsView('grouped')}
                            >
                                Grouped
                            </button>
                        </div>
                        <AdminDashboardPaginatedSection
                            rows={dashboard.pricingConfigs}
                            page={dashboard.activePage}
                            pageSize={PAGE_SIZE}
                            total={dashboard.activeData?.total ?? 0}
                            totalPages={dashboard.activeData?.totalPages ?? 1}
                            onPageChange={dashboard.setActivePage}
                            render={(rows) => (
                                <AdminPricingTable
                                    rows={rows}
                                    view={dashboard.pricingConfigsView}
                                    onRowClick={dashboard.setSelectedPricingConfig}
                                    onOpenActiveUsage={dashboard.openActivePricingUsageModal}
                                    onArchivePricingConfig={dashboard.openArchivePricingConfirm}
                                />
                            )}
                        />
                    </div>
                )}
            {dashboard.shouldFetchActiveTab &&
                !dashboard.isLoading &&
                !dashboard.error &&
                dashboard.activeTab === 'professionalServices' && (
                    <AdminDashboardPaginatedSection
                        rows={dashboard.professionalServices}
                        page={dashboard.activePage}
                        pageSize={PAGE_SIZE}
                        total={dashboard.activeData?.total ?? 0}
                        totalPages={dashboard.activeData?.totalPages ?? 1}
                        onPageChange={dashboard.setActivePage}
                        render={(rows) => (
                            <ProfessionalServicesTableSection
                                rows={rows}
                                onRowClick={dashboard.handleProfessionalServiceRowClick}
                                onDelete={dashboard.handleProfessionalServiceDelete}
                                deletingId={dashboard.professionalServiceDeleteTarget?.id ?? null}
                            />
                        )}
                    />
                )}

            <AddPricingConfigDialog
                open={dashboard.isPricingDialogOpen}
                onClose={() => dashboard.setIsPricingDialogOpen(false)}
                onCreated={() => dashboard.showSuccessToast('Pricing configuration created.')}
                pricingConfigs={dashboard.pricingConfigs}
            />
            <EditPricingConfigDialog
                open={dashboard.selectedPricingConfig !== null}
                pricingConfig={dashboard.selectedPricingConfig}
                onClose={() => dashboard.setSelectedPricingConfig(null)}
                onCreated={() =>
                    dashboard.showSuccessToast('Pricing configuration version created.')
                }
            />
            <PricingConfigActiveUsageDialog
                open={dashboard.activePricingUsageModal !== null}
                description={dashboard.activePricingUsageModal?.description}
                isLoading={dashboard.activePricingUsageQuery.isLoading}
                sows={dashboard.activePricingUsageQuery.data?.sows ?? []}
                onClose={() => dashboard.setActivePricingUsageModal(null)}
            />
            <PricingConfigArchiveConfirmDialog
                open={dashboard.archivePricingConfirmId !== null}
                versionSubtitle={
                    dashboard.archivePricingConfirmRow
                        ? `${dashboard.archivePricingConfirmRow.name} v${dashboard.archivePricingConfirmRow.version}`
                        : undefined
                }
                isPending={dashboard.deactivatePricingConfig.isPending}
                onCancel={() => dashboard.setArchivePricingConfirmId(null)}
                onConfirm={() => void dashboard.confirmArchivePricingConfig()}
            />
            <ProfessionalServiceDialog
                open={dashboard.isProfessionalServiceDialogOpen}
                professionalService={dashboard.selectedProfessionalService}
                onClose={() => {
                    dashboard.setIsProfessionalServiceDialogOpen(false);
                    dashboard.setSelectedProfessionalService(null);
                }}
                onSaved={(mode) =>
                    dashboard.showSuccessToast(
                        mode === 'create'
                            ? 'Professional service created.'
                            : 'Professional service updated.',
                    )
                }
            />
            <ProfessionalServiceDeleteConfirmDialog
                open={dashboard.professionalServiceDeleteTarget !== null}
                professionalServiceName={dashboard.professionalServiceDeleteTarget?.name ?? null}
                professionalServiceId={dashboard.professionalServiceDeleteTarget?.id ?? null}
                onClose={() => dashboard.setProfessionalServiceDeleteTarget(null)}
                onDeleted={() => dashboard.showSuccessToast('Professional service deleted.')}
            />
            <AdminCreateCompanyModal
                open={dashboard.isCreateCompanyModalOpen}
                onClose={() => dashboard.setIsCreateCompanyModalOpen(false)}
                onCreated={dashboard.handleCompanyCreated}
            />
            <AdminCreateUserModal
                open={dashboard.isCreateUserModalOpen}
                onClose={() => dashboard.setIsCreateUserModalOpen(false)}
                onCreated={dashboard.handleUserCreated}
            />
            <AdminEditUserModal
                open={dashboard.selectedUser !== null}
                user={dashboard.selectedUser}
                onClose={() => dashboard.setSelectedUser(null)}
                onSaved={(result) =>
                    result.auth0SyncWarning
                        ? dashboard.showWarningToast(result.auth0SyncWarning)
                        : dashboard.showSuccessToast('User updated.')
                }
            />
            <AdminToast toast={dashboard.toast} onClose={dashboard.dismissToast} />
        </div>
    );
}
